import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import StatCard from '../../components/charts/StatCard';
import AdvancedFilters from '../../components/common/AdvancedFilters';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import AssignTicketModal from '../../components/features/tickets/AssignTicketModal';
import TLEditTicketModal from '../../components/features/tickets/TLEditTicketModal';
import AddTicketModal from '../../components/features/tickets/AddTicketModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import ticketService from '../../services/ticketService';
import engineerService from '../../services/engineerService';
import siteService from '../../services/siteService';
import { useAuth } from '../../hooks/useAuth';
import { useSite } from '../../contexts/SiteContext';
import { formatDateTime } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS, TICKET_STATUSES } from '../../constants/statuses';
import { ROLES } from '../../constants/roles';

const TicketManagementView = () => {
  const { user } = useAuth();
  const { selectedSite } = useSite();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [engineers, setEngineers] = useState([]);
  const [sites, setSites] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({});

  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadTickets();
    loadEngineers();
    loadSites();
  }, [user?.id]);

  const loadTickets = async () => {
    setLoading(true);
    const result = await ticketService.getAllTickets();
    if (result.success) {
      setTickets(result.data || []);
    } else {
      error(result.error || 'Failed to load tickets');
    }
    setLoading(false);
  };

  const loadEngineers = async () => {
    const teamLeadId = user?.role === ROLES.TL ? user.id : null;
    const result = await engineerService.getAllEngineers(teamLeadId);
    if (result.success) {
      setEngineers(result.data || []);
    }
  };

  const loadSites = async () => {
    const result = await siteService.getAllSites();
    if (result.success) {
      const formattedSites = (result.data || []).map(site => ({
        value: site.id.toString(),
        label: site.name
      }));
      setSites(formattedSites);
    }
  };

  const filteredTickets = useMemo(() => {
    let filtered = [...tickets];

    if (selectedSite !== 0 && selectedSite !== null) {
      filtered = filtered.filter((ticket) => ticket.site_id === selectedSite || ticket.site_id === String(selectedSite));
    }

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((ticket) => (
        ticket.title?.toLowerCase().includes(query)
        || ticket.id?.toString().includes(query)
        || ticket.system_type?.toLowerCase().includes(query)
      ));
    }

    if (advancedFilters.status) {
      if(advancedFilters.status === 'OPEN'){
        filtered = filtered.filter((ticket) => [
          TICKET_STATUSES.PENDING_ASSIGNMENT,
          TICKET_STATUSES.PENDING,
          TICKET_STATUSES.ASSIGNED,
          TICKET_STATUSES.WORK_IN_PROGRESS,
          TICKET_STATUSES.REASSIGNED,
          TICKET_STATUSES.REOPENED
        ].includes(ticket.status));
      }else if (advancedFilters.status === 'CLOSED'){
        filtered = filtered.filter((ticket) => ticket.status === 'CLOSED');
      }else {
      filtered = filtered.filter((ticket) => ticket.status === advancedFilters.status);
    }
    }

    if (advancedFilters.priority) {
      filtered = filtered.filter((ticket) => ticket.priority === advancedFilters.priority);
    }
    if (advancedFilters.assignee) {
      filtered = filtered.filter((ticket) => ticket.assignee_id?.toString() === advancedFilters.assignee);
    }
    if (advancedFilters.site) {
      filtered = filtered.filter((ticket) => ticket.site_id?.toString() === advancedFilters.site);
    }
    if (advancedFilters.startDate) {
      filtered = filtered.filter((ticket) => new Date(ticket.created_at) >= new Date(advancedFilters.startDate));
    }
    if (advancedFilters.endDate) {
      const endDate = new Date(advancedFilters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((ticket) => new Date(ticket.created_at) <= endDate);
    }

    return filtered;
  }, [tickets, searchTerm, advancedFilters, selectedSite]);

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => {
    setAdvancedFilters({});
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
  };

  const handleAssignTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowAssignModal(true);
  };

  const handleAddTicket = async (ticketData, attachments = []) => {
    const result = await ticketService.createTicket(ticketData);
    
    if (result.success) {
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          await ticketService.uploadAttachment(result.data.id, attachment.file);
        }
        success('Ticket created with attachments!');
      } else {
        success('Ticket created successfully!');
      }
      
      await loadTickets();
    } else {
      error(result.error || 'Failed to create ticket');
    }
  };

  const handleSubmitEdit = async (ticketId, ticketData) => {
    const updateResult = await ticketService.updateTicket(ticketId, ticketData);
    if (!updateResult.success) {
      error(updateResult.error || 'Failed to update ticket');
      throw new Error(updateResult.error || 'Failed to update ticket');
    }

    success('Ticket updated successfully!');
    await loadTickets();
  };

  const handleSubmitAssign = async (ticketId, engineerId, timeToAttend) => {
    const result = await ticketService.assignTicket(ticketId, engineerId, timeToAttend || null);
    if (!result.success) {
      error(result.error || 'Failed to assign ticket');
      throw new Error(result.error || 'Failed to assign ticket');
    }

    success('Ticket assigned successfully!');
    await loadTickets();
  };

  const columns = [
    {
      key: 'id',
      label: 'Ticket ID',
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {row.system_type}
          </div>
        </div>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span
          style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            background: `${PRIORITY_COLORS[value] || '#6b7280'}20`,
            color: PRIORITY_COLORS[value] || '#6b7280'
          }}
        >
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span
          style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            background: `${STATUS_COLORS[value] || '#6b7280'}20`,
            color: STATUS_COLORS[value] || '#6b7280'
          }}
        >
          {value?.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'assignee_name',
      label: 'Assignee',
      render: (value) => value || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Unassigned</span>
    },
    {
      key: 'site_name',
      label: 'Site',
      condition: () => selectedSite === 0
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => formatDateTime(value)
    }
  ];


  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  };

  const filterGroupStyle = {
    flex: '1',
    minWidth: '200px'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading tickets...
      </div>
    );
  }

  return (
    <div>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={gridStyle}>
        <StatCard title="Total Tickets" value={filteredTickets.length} icon="" color="#3b82f6" 
        onClick={() => setAdvancedFilters({})}/>
        <StatCard title="Showing" value={filteredTickets.length} icon="" subtitle="After search filters" color="var(--primary)" />
        <StatCard
          title="Open"
          value={filteredTickets.filter((ticket) => [
            TICKET_STATUSES.PENDING_ASSIGNMENT,
            TICKET_STATUSES.ASSIGNED,
            TICKET_STATUSES.WORK_IN_PROGRESS,
            TICKET_STATUSES.PENDING,
            TICKET_STATUSES.REASSIGNED,
            TICKET_STATUSES.REOPENED
          ].includes(ticket.status)).length}
          icon=""
          subtitle="Active tickets"
          color="#f59e0b"
          onClick={() => setAdvancedFilters({ status: 'OPEN' })}
        />
        <StatCard
          title="Closed"
          value={filteredTickets.filter((ticket) => ticket.status === 'CLOSED').length}
          icon=""
          subtitle="Completed"
          color="#10b981"
          onClick={() => setAdvancedFilters({ status: 'CLOSED' })}
        />
      </div>

      <div style={filterBarStyle}>
        <div style={filterGroupStyle}>
          <Input
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <AdvancedFilters
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            engineers={engineers}
            sites={sites}
          />
          <Button variant="secondary" onClick={loadTickets}>
            Refresh
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setShowAddModal(true)}
            disabled={selectedSite === 0 || !selectedSite}
            title={selectedSite === 0 ? "Select a specific site to create a ticket" : ""}
          >
            New Ticket
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns.filter(col => typeof col.condition === 'function' ? col.condition() : true)}
        data={filteredTickets}
        onRowClick={handleViewTicket}
        actions={[
          { label: ' View', onClick: handleViewTicket },
          { 
            label: ' Edit', 
            onClick: handleEditTicket,
            condition: (ticket) => ticket.status !== TICKET_STATUSES.CLOSED
          },
          { 
            label: ' Assign', 
            onClick: handleAssignTicket,
            condition: (ticket) => ticket.status !== TICKET_STATUSES.CLOSED
          }
        ]}
        pageSize={15}
      />

      <ViewTicketModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
      />

      <TLEditTicketModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTicket(null);
        }}
        onSubmit={handleSubmitEdit}
        ticket={selectedTicket}
      />

      <AssignTicketModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedTicket(null);
        }}
        onSubmit={handleSubmitAssign}
        ticket={selectedTicket}
      />

      {showAddModal && (
        <AddTicketModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddTicket}
        />
      )}
    </div>
  );
};

export default TicketManagementView;
